import {
  ArrowBack,
  ArrowUpward,
  AttachFile,
  AutoAwesome,
  Close,
  Description,
  Download,
  OpenInFull,
  OpenInNew,
  Person,
  Psychology,
  Support,
  Warning,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import PopOutTextDialog from "../../components/PopOutTextDialog";
import PopOutTextInput from "../../components/PopOutTextInput";
import { hostname, parseError, isSafeUrl } from "../../utils";
import StatusChip, { TicketStatus } from "./components/StatusChip";

interface AIAnalysis {
  canBeFixedWithCode: boolean;
  confidenceScore: number;
  suggestedAction: string;
  isMalicious: boolean;
  reasoning: string;
  ticketId?: string;
}

// Attachment types from backend
interface PublicImageAttachment {
  type: "image";
  url: string;  // Legacy public images have URL directly
}

interface PrivateImageAttachment {
  type: "image";
  id: string;   // Private images have ID for authenticated access
  name: string;
}

interface DocumentAttachment {
  type: "document";
  id: string;
  name: string;
  contentType?: string;
}

type ImageAttachment = PublicImageAttachment | PrivateImageAttachment;
type TicketAttachment = ImageAttachment | DocumentAttachment;

// Type guards
const isPublicImage = (attachment: ImageAttachment): attachment is PublicImageAttachment => {
  return "url" in attachment;
};

const isPrivateImage = (attachment: ImageAttachment): attachment is PrivateImageAttachment => {
  return "id" in attachment && !("url" in attachment);
};

interface SupportTicket {
  id: string;
  companyId: string;
  userId: string;
  whitelabelId?: string;
  subject: string;
  description: string;
  severity: "low" | "medium" | "high" | "critical";
  status: TicketStatus;
  ticketType: "bug" | "feature_request" | "question" | "other";
  attachments: TicketAttachment[];
  response?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
  updatedAt: string;
  userEmail?: string;
  userName?: string;
  resolverEmail?: string;
  resolverName?: string;
  companyName?: string;
  whitelabelName?: string;
  // AI Analysis fields
  aiAnalysis?: AIAnalysis;
  canBeFixedWithCode?: boolean;
  analysisConfidenceScore?: number;
  relatedCodeTaskId?: string;
  analyzedAt?: string;
  // Roadmap fields
  roadmapStatus?: "none" | "near_term" | "mid_term" | "long_term" | "released";
  roadmapTitle?: string;
  roadmapDescription?: string;
}

interface TicketMessage {
  id: string;
  ticketId: string;
  userId: string;
  message: string;
  isFromSupport: boolean;
  attachments: string[];
  createdAt: string;
  updatedAt: string;
  userEmail?: string;
  userName?: string;
}

export default function SupportTicketDetail({
  getToken,
  setError,
  setSuccess,
  ticketId,
  onBack,
  isSuperUser = false,
  isWhitelabelOwner = false,
}: {
  getToken: () => Promise<string>;
  setError: (error: string) => void;
  setSuccess: (message: string) => void;
  ticketId: string;
  onBack?: () => void;
  isSuperUser?: boolean;
  isWhitelabelOwner?: boolean;
}) {
  const theme = useTheme();
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [screenshotViewerOpen, setScreenshotViewerOpen] = useState(false);
  const [messageAttachments, setMessageAttachments] = useState<string[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [imageDataUrls, setImageDataUrls] = useState<{[key: string]: string}>({});
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [viewingImageUrl, setViewingImageUrl] = useState<string>("");
  const [popOutDialogOpen, setPopOutDialogOpen] = useState(false);

  // Admin controls
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editForm, setEditForm] = useState({
    subject: "",
    description: "",
    status: "",
    severity: "",
    ticketType: "",
    roadmapStatus: "none",
    roadmapTitle: "",
    roadmapDescription: "",
  });

  // AI Analysis states
  const [analyzing, setAnalyzing] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Support staff can manage ticket status (super users or whitelabel owners)
  const canManageTicket = isSuperUser || isWhitelabelOwner;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadCurrentUser = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurrentUserId(response.data.usage.uid);
    } catch (err) {
      // If we can't get user info, we'll fall back to isFromSupport logic
      console.warn("Could not get current user ID:", parseError(err));
    }
  };

  const loadTicket = async () => {
    if (!ticketId) return;

    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/support/tickets/${ticketId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTicket(response.data);
    } catch (err) {
      setError(parseError(err));
    }
    setLoading(false);
  };

  const loadMessages = async (showLoading = true) => {
    if (!ticketId) return;

    if (showLoading) {
      setMessagesLoading(true);
    }
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/support/tickets/${ticketId}/messages`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setMessages(response.data.messages || []);
    } catch (err) {
      setError(parseError(err));
    }
    setMessagesLoading(false);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !ticketId) return;

    setSending(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/support/tickets/${ticketId}/messages`,
        {
          message: newMessage,
          attachments: messageAttachments
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNewMessage("");
      setMessageAttachments([]);
      setImageDataUrls({});
      await loadMessages(false);
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      setError(parseError(err));
    }
    setSending(false);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingFiles(true);
    try {
      const token = await getToken();
      const uploadedIds: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validate file type - only allow images
        if (!file.type.startsWith("image/")) {
          setError(
            `File "${file.name}" is not an image. Please upload only image files.`
          );
          continue;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setError(
            `Image "${file.name}" is too large. Please keep images under 5MB.`
          );
          continue;
        }

        const formData = new FormData();
        formData.append("image", file);
        formData.append("name", `Support Message - ${file.name}`);
        formData.append("category", "support-message");
        formData.append("public", "true"); // Make message attachments public so they use /p/ endpoint

        const uploadResponse = await axios.post(
          `${hostname}/platform-images`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (uploadResponse.status === 201) {
          const imageId = uploadResponse.data.id;
          const imageUrl = uploadResponse.data.url;

          // Store the image ID to base64 mapping
          setImageDataUrls(prev => ({ ...prev, [imageId]: imageUrl }));

          // Store the image ID for the attachments array
          uploadedIds.push(imageId);
        }
      }

      // Update message attachments with new images
      setMessageAttachments(prev => [...prev, ...uploadedIds]);

      // Clear file input
      event.target.value = "";
    } catch (err) {
      setError(`Failed to upload files: ${parseError(err)}`);
    }
    setUploadingFiles(false);
  };

  const removeMessageAttachment = (index: number) => {
    setMessageAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const quickStatusChange = async (newStatus: string) => {
    if (!ticket || !canManageTicket) return;

    setUpdating(true);
    try {
      const token = await getToken();
      const response = await axios.put(
        `${hostname}/support/tickets/${ticketId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTicket(response.data);
      setSuccess(`Ticket status updated to ${newStatus.replace("_", " ")}`);
    } catch (err) {
      setError(parseError(err));
    }
    setUpdating(false);
  };

  const openEditDialog = () => {
    if (!ticket) return;

    setEditForm({
      subject: ticket.subject,
      description: ticket.description,
      status: ticket.status,
      severity: ticket.severity,
      ticketType: ticket.ticketType,
      roadmapStatus: ticket.roadmapStatus || "none",
      roadmapTitle: ticket.roadmapTitle || "",
      roadmapDescription: ticket.roadmapDescription || "",
    });
    setEditDialogOpen(true);
  };

  const updateTicket = async () => {
    if (!ticket || !canManageTicket) return;

    setUpdating(true);
    try {
      const token = await getToken();
      const response = await axios.put(
        `${hostname}/support/tickets/${ticketId}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTicket(response.data);
      setEditDialogOpen(false);
      setSuccess("Ticket updated successfully");
    } catch (err) {
      setError(parseError(err));
    }
    setUpdating(false);
  };

  const analyzeTicket = async () => {
    if (!ticket || !isSuperUser) return;

    setAnalyzing(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/support/tickets/${ticketId}/analyze`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTicket(response.data.ticket);
      setSuccess("Ticket analyzed successfully");

    } catch (err) {
      setError(parseError(err));
    }
    setAnalyzing(false);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  // Helper to open a document attachment through the authenticated endpoint
  const openDocument = async (documentId: string, fileName: string) => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/support/documents/${documentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      // Create a blob URL and open in new tab
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const safeUrlPattern = /^blob:https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
      if (safeUrlPattern.test(url)) window.open(url, "_blank");

      // Clean up the blob URL after a short delay
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(`Failed to open document: ${parseError(err)}`);
    }
  };

  // Helper to download a document attachment
  const downloadDocument = async (documentId: string, fileName: string) => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/support/documents/${documentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();

      // Clean up the blob URL after a short delay
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(`Failed to download document: ${parseError(err)}`);
    }
  };

  // Cache for private image blob URLs
  const [privateImageUrls, setPrivateImageUrls] = useState<Record<string, string>>({});

  // Helper to get private image URL through the authenticated endpoint
  const getPrivateImageUrl = async (imageId: string): Promise<string> => {
    // Check cache first
    if (privateImageUrls[imageId]) {
      return privateImageUrls[imageId];
    }

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/platform-images/pr/${imageId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      // Create a blob URL
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);

      // Cache it
      setPrivateImageUrls((prev) => ({ ...prev, [imageId]: url }));

      return url;
    } catch (err) {
      console.error(`Failed to fetch private image: ${parseError(err)}`);
      return "";
    }
  };

  // Load private images when ticket loads
  useEffect(() => {
    if (!ticket?.attachments) return;

    const loadPrivateImages = async () => {
      for (const attachment of ticket.attachments) {
        if (attachment.type === "image" && isPrivateImage(attachment)) {
          await getPrivateImageUrl(attachment.id);
        }
      }
    };

    loadPrivateImages();

    // Cleanup blob URLs on unmount
    return () => {
      Object.values(privateImageUrls).forEach((url) => {
        window.URL.revokeObjectURL(url);
      });
    };
  }, [ticket?.attachments]);

  useEffect(() => {
    loadCurrentUser();
    loadTicket();
    loadMessages();
  }, [ticketId]);

  // Lightweight polling for new messages every 2 seconds
  useEffect(() => {
    if (!ticketId) return;

    const checkForNewMessages = async () => {
      try {
        const token = await getToken();
        const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null;

        const response = await axios.get(
          `${hostname}/support/tickets/${ticketId}/messages/check`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: lastMessageId ? { sinceId: lastMessageId } : {},
          }
        );

        // Only fetch full messages if there are new ones
        if (response.data.hasNew) {
          await loadMessages(false);
        }
      } catch (err) {
        // Silently fail on polling errors
        console.warn("Failed to check for new messages:", err);
      }
    };

    const intervalId = setInterval(checkForNewMessages, 5000);

    return () => clearInterval(intervalId);
  }, [ticketId, messages, getToken]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (loading && !ticket) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header Skeleton */}
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={32} />
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Skeleton variant="rounded" width={80} height={24} />
              <Skeleton variant="rounded" width={60} height={24} />
              <Skeleton variant="rounded" width={100} height={24} />
            </Box>
          </Box>
        </Box>

        {/* Original Issue Card Skeleton */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Skeleton variant="text" width="30%" height={28} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="40%" sx={{ mt: 2 }} />
          </CardContent>
        </Card>

        {/* Messages Section Skeleton */}
        <Paper sx={{ minHeight: 400 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Skeleton variant="text" width="20%" height={28} />
          </Box>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }} color="text.secondary">
                Loading ticket details...
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (!ticket) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography>Ticket not found</Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        mt: 4,
        mb: 4,
        display: "flex",
        flexDirection: "column",
        minHeight: "calc(100vh - 120px)",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton
          onClick={() => (onBack ? onBack() : window.history.back())}
          color="primary"
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Typography variant="h5" component="h1">
              {ticket.subject}
            </Typography>
            {canManageTicket && (
              <Button
                size="small"
                variant="outlined"
                onClick={openEditDialog}
                disabled={updating}
              >
                Edit
              </Button>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <StatusChip status={ticket.status} />
            <Chip
              label={ticket.severity.toUpperCase()}
              size="small"
              color={getSeverityColor(ticket.severity) as any}
            />
            <Chip
              label={ticket.ticketType.replace("_", " ").toUpperCase()}
              size="small"
              variant="outlined"
            />
            {ticket.companyName && (
              <Typography variant="body2" color="text.secondary">
                Company: {ticket.companyName}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Created: {new Date(ticket.createdAt).toLocaleString()}
            </Typography>
          </Box>

          {/* Admin Quick Actions */}
          {canManageTicket && ticket.status !== "closed" && (
            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ alignSelf: "center", mr: 1 }}
              >
                Quick actions:
              </Typography>
              {(ticket.status as TicketStatus) === "open" && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => quickStatusChange("in_progress")}
                  disabled={updating}
                >
                  Start Work
                </Button>
              )}
              {((ticket.status as TicketStatus) === "open" ||
                (ticket.status as TicketStatus) === "in_progress") && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => quickStatusChange("resolved")}
                  disabled={updating}
                >
                  Mark Resolved
                </Button>
              )}
              {(ticket.status as TicketStatus) === "resolved" && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => quickStatusChange("closed")}
                  disabled={updating}
                >
                  Close
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* AI Analysis Section for Super Users */}
      {isSuperUser && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Psychology color="primary" />
                <Typography variant="h6">AI Analysis</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                {!ticket.aiAnalysis && (
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    onClick={analyzeTicket}
                    loading={analyzing}
                    startIcon={<AutoAwesome />}
                  >
                    Analyze Ticket
                  </LoadingButton>
                )}
              </Box>
            </Box>

            {ticket.aiAnalysis ? (
              <Box>
                {/* Malicious Warning */}
                {ticket.aiAnalysis.isMalicious && (
                  <Box
                    sx={{
                      p: 2,
                      mb: 2,
                      bgcolor: "error.light",
                      color: "error.contrastText",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Warning />
                    <Typography variant="body2">
                      This request appears to be potentially malicious or
                      harmful
                    </Typography>
                  </Box>
                )}

                {/* Analysis Results */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Can be fixed with code:
                    </Typography>
                    <Chip
                      label={
                        ticket.aiAnalysis.canBeFixedWithCode ? "Yes" : "No"
                      }
                      color={
                        ticket.aiAnalysis.canBeFixedWithCode
                          ? "success"
                          : "default"
                      }
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Confidence Score:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 200,
                          height: 8,
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${
                              ticket.aiAnalysis.confidenceScore * 100
                            }%`,
                            height: "100%",
                            bgcolor:
                              ticket.aiAnalysis.confidenceScore > 0.7
                                ? "success.main"
                                : ticket.aiAnalysis.confidenceScore > 0.4
                                ? "warning.main"
                                : "error.main",
                            transition: "width 0.3s",
                          }}
                        />
                      </Box>
                      <Typography variant="body2">
                        {(ticket.aiAnalysis.confidenceScore * 100).toFixed(0)}%
                      </Typography>
                    </Box>
                  </Box>

                  {ticket.aiAnalysis.suggestedAction && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Suggested Action:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {ticket.aiAnalysis.suggestedAction}
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Reasoning:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                    >
                      {ticket.aiAnalysis.reasoning}
                    </Typography>
                  </Box>

                  {ticket.analyzedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Analyzed: {new Date(ticket.analyzedAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No AI analysis available yet. Click "Analyze Ticket" to generate
                analysis.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Original Ticket Description */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Original Issue
          </Typography>
          {ticket.companyName && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Company: {ticket.companyName}
            </Typography>
          )}
          {ticket.userEmail && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Email: {ticket.userEmail}
            </Typography>
          )}
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
            {ticket.description}
          </Typography>

          {/* Attachments (Images and Documents) */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Attachments ({ticket.attachments.length})
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {ticket.attachments.map((attachment, index) => {
                  if (attachment.type === "image") {
                    // Determine if it's a public or private image
                    const imageUrl = isPublicImage(attachment)
                      ? attachment.url
                      : privateImageUrls[attachment.id] || "";

                    return (
                      <Box
                        key={index}
                        sx={{
                          width: 100,
                          height: 100,
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          overflow: "hidden",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: !imageUrl ? "action.hover" : "transparent",
                          "&:hover": {
                            boxShadow: 2,
                          },
                        }}
                        onClick={() => {
                          if (imageUrl) {
                            setViewingImageUrl(imageUrl);
                            setImageViewerOpen(true);
                          }
                        }}
                      >
                        {imageUrl ? (
                          <img
                            src={/^https?:\/\/[a-zA-Z0-9]/.test(imageUrl) ? imageUrl : ""}
                            alt={`Screenshot ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <CircularProgress size={24} />
                        )}
                      </Box>
                    );
                  } else {
                    // Document attachment - fetch through authenticated endpoint
                    return (
                      <Box
                        key={index}
                        sx={{
                          minWidth: 120,
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          backgroundColor: "action.hover",
                          p: 1,
                        }}
                      >
                        <Box
                          sx={{
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            "&:hover": {
                              opacity: 0.8,
                            },
                          }}
                          onClick={() => openDocument(attachment.id, attachment.name)}
                        >
                          <Description
                            sx={{ fontSize: 32, color: "primary.main", mb: 0.5 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              textAlign: "center",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                            }}
                            title={attachment.name}
                          >
                            {attachment.name.length > 15
                              ? attachment.name.substring(0, 12) + "..."
                              : attachment.name}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadDocument(attachment.id, attachment.name);
                          }}
                          sx={{ mt: 0.5 }}
                          title="Download"
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  }
                })}
              </Box>
            </Box>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            Reported by {ticket.userName || ticket.userEmail || "Unknown"} on{" "}
            {new Date(ticket.createdAt).toLocaleDateString()}
          </Typography>
        </CardContent>
      </Card>

      {/* Messages */}
      <Paper
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 400,
        }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid #eee" }}>
          <Typography variant="h6">Conversation</Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {messagesLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Loading skeletons for messages */}
              {[1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    justifyContent: i % 2 === 0 ? "flex-end" : "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: i % 2 === 0 ? "row-reverse" : "row",
                      alignItems: "flex-start",
                      gap: 1,
                      maxWidth: "70%",
                    }}
                  >
                    <Skeleton variant="circular" width={32} height={32} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton
                        variant="rounded"
                        width={i % 2 === 0 ? 200 : 280}
                        height={60}
                      />
                      <Skeleton variant="text" width={120} sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                </Box>
              ))}
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  Loading messages...
                </Typography>
              </Box>
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: "center", color: "text.secondary", py: 4 }}>
              <Typography>No messages yet. Start the conversation!</Typography>
            </Box>
          ) : (
            messages.map((message) => {
              // Simple rule: messages from ticket creator go on right, others go on left
              const isFromTicketCreator = message.userId === ticket?.userId;
              const showOnLeft = !isFromTicketCreator;

              return (
                <Box
                  key={message.id}
                  sx={{
                    display: "flex",
                    justifyContent: showOnLeft ? "flex-start" : "flex-end",
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "70%",
                      display: "flex",
                      flexDirection: showOnLeft ? "row" : "row-reverse",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                      }}
                    >
                      {showOnLeft ? (
                        <Support fontSize="small" />
                      ) : (
                        <Person fontSize="small" />
                      )}
                    </Avatar>
                  <Box>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {message.message}
                      </Typography>

                      {/* Message attachments */}
                      {message.attachments && message.attachments.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            Attachments ({message.attachments.length}):
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                            {message.attachments.map((imageUrl, imgIndex) => (
                              <Box
                                key={imgIndex}
                                sx={{
                                  width: 120,
                                  height: 120,
                                  border: 1,
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  cursor: "pointer",
                                  "&:hover": {
                                    boxShadow: 2,
                                  },
                                }}
                                onClick={() => {
                                  setViewingImageUrl(imageUrl);
                                  setImageViewerOpen(true);
                                }}
                              >
                                <img
                                  src={/^https?:\/\/[a-zA-Z0-9]/.test(imageUrl) ? imageUrl : ""}
                                  alt={`Message attachment ${imgIndex + 1}`}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Paper>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        textAlign: showOnLeft ? "left" : "right",
                      }}
                    >
                      {message.userName || message.userEmail || "Unknown"} •{" "}
                      {new Date(message.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Message Input */}
        <Divider />
        <Box sx={{ p: 2 }}>
          {/* Attachment thumbnails */}
          {messageAttachments.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Attachments ({messageAttachments.length}):
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {messageAttachments.map((attachmentId, index) => {
                  const imageUrl = imageDataUrls[attachmentId] || '';
                  return (
                    <Box
                      key={index}
                      sx={{
                        position: "relative",
                        width: 60,
                        height: 60,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      {imageUrl ? (
                        <img
                          src={/^https?:\/\/[a-zA-Z0-9]/.test(imageUrl) ? imageUrl : ""}
                          alt={`Attachment ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "action.hover",
                            fontSize: "10px",
                          }}
                        >
                          Loading...
                        </Box>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => removeMessageAttachment(index)}
                        sx={{
                          position: "absolute",
                          top: -4,
                          right: -4,
                          bgcolor: "background.paper",
                          opacity: 0.9,
                          "&:hover": {
                            bgcolor: "background.paper",
                            opacity: 1,
                          },
                          width: 20,
                          height: 20,
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Hidden file input */}
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileUpload}
            style={{ display: "none" }}
            id="message-file-upload-input"
          />

          {/* Chat-style input container */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
              p: 1.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              "&:focus-within": {
                borderColor: "primary.main",
                borderWidth: "2px",
              },
            }}
          >
            {/* Attachment button on the left */}
            <label htmlFor="message-file-upload-input">
              <IconButton
                component="span"
                disabled={uploadingFiles}
                sx={{
                  color: "text.secondary",
                  flexShrink: 0,
                }}
              >
                {uploadingFiles ? (
                  <CircularProgress size={24} />
                ) : (
                  <AttachFile />
                )}
              </IconButton>
            </label>

            {/* Text input in the middle */}
            <Box sx={{ flex: 1, position: "relative" }}>
              <PopOutTextInput
                fullWidth
                multiline
                rows={2}
                value={newMessage}
                onChange={(value) => setNewMessage(value)}
                placeholder="Type your message..."
                dialogTitle="Compose Message"
                dialogDescription="Write your message to support or the user."
                popOutRows={10}
                enablePopOut={false}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    padding: 0,
                  },
                }}
              />

              {/* Expand button - only show when text reaches 2 rows */}
              {newMessage.split('\n').length >= 2 && (
                <IconButton
                  onClick={() => setPopOutDialogOpen(true)}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    color: theme.palette.text.secondary,
                    opacity: 0.6,
                    zIndex: 2,
                    "&:hover": {
                      opacity: 1,
                    },
                  }}
                >
                  <OpenInFull fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Send button on the right */}
            <IconButton
              onClick={sendMessage}
              disabled={!newMessage.trim() || sending}
              sx={{
                backgroundColor: theme.palette.mode === "dark" ? "#ffffff" : "#000000",
                color: theme.palette.mode === "dark" ? "#000000" : "#ffffff",
                width: 40,
                height: 40,
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: theme.palette.mode === "dark" ? "#f0f0f0" : "#333333",
                },
                "&:disabled": {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              {sending ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <ArrowUpward />
              )}
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Screenshot Viewer Dialog */}
      <Dialog
        open={screenshotViewerOpen}
        onClose={() => setScreenshotViewerOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Attachments for: {ticket.subject}
          <IconButton
            onClick={() => setScreenshotViewerOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {ticket.attachments && ticket.attachments.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              }}
            >
              {ticket.attachments.map((attachment, index) => {
                if (attachment.type === "image") {
                  // Determine if it's a public or private image
                  const imageUrl = isPublicImage(attachment)
                    ? attachment.url
                    : privateImageUrls[attachment.id] || "";

                  return (
                    <Box
                      key={index}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                        backgroundColor: "action.hover",
                        cursor: imageUrl ? "pointer" : "default",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 200,
                        "&:hover": {
                          boxShadow: imageUrl ? 2 : 0,
                        },
                      }}
                      onClick={() => {
                        if (imageUrl) {
                          setViewingImageUrl(imageUrl);
                          setImageViewerOpen(true);
                        }
                      }}
                    >
                      {imageUrl ? (
                        <>
                          <img
                            src={/^https?:\/\/[a-zA-Z0-9]/.test(imageUrl) ? imageUrl : ""}
                            alt={`Screenshot ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "auto",
                              display: "block",
                              maxHeight: "400px",
                              objectFit: "contain",
                            }}
                          />
                          <Box sx={{ p: 1, bgcolor: "background.paper", width: "100%" }}>
                            <Typography variant="caption" color="text.secondary">
                              Screenshot {index + 1}
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <CircularProgress />
                      )}
                    </Box>
                  );
                } else {
                  return (
                    <Box
                      key={index}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                        backgroundColor: "action.hover",
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          "&:hover": {
                            opacity: 0.8,
                          },
                        }}
                        onClick={() => openDocument(attachment.id, attachment.name)}
                      >
                        <Description
                          sx={{ fontSize: 48, color: "primary.main", mb: 1 }}
                        />
                        <Typography variant="body2">{attachment.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click to open
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        startIcon={<Download />}
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadDocument(attachment.id, attachment.name);
                        }}
                        sx={{ mt: 1 }}
                      >
                        Download
                      </Button>
                    </Box>
                  );
                }
              })}
            </Box>
          ) : (
            <Typography>No attachments available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScreenshotViewerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Ticket Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Ticket</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <PopOutTextInput
              fullWidth
              value={editForm.subject}
              onChange={(value) =>
                setEditForm((prev) => ({ ...prev, subject: value }))
              }
              placeholder="Ticket subject"
              label="Subject"
              dialogTitle="Edit Subject"
              dialogDescription="Update the ticket subject"
            />

            <PopOutTextInput
              fullWidth
              multiline
              rows={4}
              value={editForm.description}
              onChange={(value) =>
                setEditForm((prev) => ({ ...prev, description: value }))
              }
              placeholder="Ticket description"
              label="Description"
              dialogTitle="Edit Description"
              dialogDescription="Update the ticket description"
              popOutRows={10}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editForm.status}
                  label="Status"
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, status: e.target.value }))
                  }
                >
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={editForm.severity}
                  label="Severity"
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      severity: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={editForm.ticketType}
                label="Type"
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    ticketType: e.target.value,
                  }))
                }
              >
                <MenuItem value="bug">Bug</MenuItem>
                <MenuItem value="feature_request">Feature Request</MenuItem>
                <MenuItem value="question">Question</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            {/* Roadmap Section - Only show for feature requests and super users */}
            {isSuperUser && editForm.ticketType === "feature_request" && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Roadmap Settings
                </Typography>

                <FormControl fullWidth>
                  <InputLabel>Roadmap Status</InputLabel>
                  <Select
                    value={editForm.roadmapStatus}
                    label="Roadmap Status"
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        roadmapStatus: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="none">Not on Roadmap</MenuItem>
                    <MenuItem value="near_term">Near Term</MenuItem>
                    <MenuItem value="mid_term">Mid Term</MenuItem>
                    <MenuItem value="long_term">Long Term</MenuItem>
                    <MenuItem value="released">Released</MenuItem>
                  </Select>
                </FormControl>

                {editForm.roadmapStatus !== "none" && (
                  <>
                    <PopOutTextInput
                      fullWidth
                      value={editForm.roadmapTitle}
                      onChange={(value) =>
                        setEditForm((prev) => ({ ...prev, roadmapTitle: value }))
                      }
                      placeholder="Custom title for roadmap (optional)"
                      label="Roadmap Title"
                      dialogTitle="Edit Roadmap Title"
                      dialogDescription="Custom title to display on the public roadmap. Leave empty to use the ticket subject."
                    />

                    <PopOutTextInput
                      fullWidth
                      multiline
                      rows={3}
                      value={editForm.roadmapDescription}
                      onChange={(value) =>
                        setEditForm((prev) => ({ ...prev, roadmapDescription: value }))
                      }
                      placeholder="Custom description for roadmap (optional)"
                      label="Roadmap Description"
                      dialogTitle="Edit Roadmap Description"
                      dialogDescription="Custom description to display on the public roadmap. Leave empty to use the ticket description."
                      popOutRows={8}
                    />
                  </>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            onClick={updateTicket}
            loading={updating}
            variant="contained"
          >
            Update Ticket
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle>
          Image Viewer
          <IconButton
            onClick={() => setImageViewerOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 0,
            overflow: "hidden",
          }}
        >
          {viewingImageUrl && (
            <img
              src={/^https?:\/\/[a-zA-Z0-9]/.test(viewingImageUrl) ? viewingImageUrl : ""}
              alt="Full size view"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/; if (safeUrlPattern.test(viewingImageUrl)) window.open(viewingImageUrl, "_blank"); }}
            startIcon={<OpenInNew />}
          >
            Open in New Tab
          </Button>
          <Button onClick={() => setImageViewerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Pop Out Text Dialog */}
      <PopOutTextDialog
        open={popOutDialogOpen}
        onClose={() => setPopOutDialogOpen(false)}
        onSave={(newValue) => {
          setNewMessage(newValue);
          setPopOutDialogOpen(false);
        }}
        value={newMessage}
        title="Compose Message"
        description="Write your message to support or the user."
        placeholder="Type your message..."
        rows={10}
      />
    </Container>
  );
}
